import { PermissionGate } from "@components/PermissionGate";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Group,
  Modal,
  NumberInput,
  ScrollAreaAutosize,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { subHours } from "date-fns";
import i18n from "src/i18n";

import { getIsTimeSlotDisabled } from "@utils/dateValidation";

import { useUpdateAppointmentSlot } from "@hooks/mutations/appointment/useUpdateAppointmentSlot";

import { AppointmentSlot } from "@customTypes/appointment";


interface EditAppointmentSlotModalProps {
  opened: boolean;
  close: () => void;
  slot: AppointmentSlot;
  slots: AppointmentSlot[];
}

export const EditAppointmentSlotModal = ({
  opened,
  slot,
  slots,
  close,
}: EditAppointmentSlotModalProps) => {
  const { t } = useTranslation();

  const [currentSlot, setCurrentSlot] =
    useState<Partial<AppointmentSlot> | null>(null);
  const [newSlot, setNewSlot] = useState<Partial<AppointmentSlot> | null>(null);

  const {
    mutate: updateAppointmentSlot,
    isLoading: isUpdateAppointmentSlotLoading,
  } = useUpdateAppointmentSlot();

  // console.log(
  //   "is time slot disabled",
  //   getIsTimeSlotDisabled(
  //     slots,
  //     subHours(
  //       currentSlot?.startTime as Date,
  //       currentSlot?.startTime?.getHours() as number
  //     ),
  //     12
  //   )
  // );

  const timeSlotData = useMemo(
    () => [
      {
        label: i18n.language === "fr" ? "8h00" : "8:00 AM",
        value: "8",
        disabled: getIsTimeSlotDisabled(
          slots,
          subHours(
            currentSlot?.startTime as Date,
            currentSlot?.startTime?.getHours() as number
          ),
          8
        ),
      },
      {
        label: i18n.language === "fr" ? "10h00" : "10:00 AM",
        value: "10",
        disabled: getIsTimeSlotDisabled(
          slots,
          subHours(
            currentSlot?.startTime as Date,
            currentSlot?.startTime?.getHours() as number
          ),
          10
        ),
      },
      {
        label: i18n.language === "fr" ? "12h00" : "12:00 PM",
        value: "12",
        disabled: getIsTimeSlotDisabled(
          slots,
          subHours(
            currentSlot?.startTime as Date,
            currentSlot?.startTime?.getHours() as number
          ),
          12
        ),
      },
      {
        label: i18n.language === "fr" ? "14h00" : "2:00 PM",
        value: "14",
        disabled: getIsTimeSlotDisabled(
          slots,
          subHours(
            currentSlot?.startTime as Date,
            currentSlot?.startTime?.getHours() as number
          ),
          14
        ),
      },
      {
        label: i18n.language === "fr" ? "16h00" : "4:00 PM",
        value: "16",
        disabled: getIsTimeSlotDisabled(
          slots,
          subHours(
            currentSlot?.startTime as Date,
            currentSlot?.startTime?.getHours() as number
          ),
          16
        ),
      },
      {
        label: i18n.language === "fr" ? "18h00" : "6:00 PM",
        value: "18",
        disabled: getIsTimeSlotDisabled(
          slots,
          subHours(
            currentSlot?.startTime as Date,
            currentSlot?.startTime?.getHours() as number
          ),
          18
        ),
      },
    ],
    [slots, currentSlot, i18n.language]
  );

  const handleUpdateAppointmentSlot = () => {
    // Conditions to not execute here...
    if (!newSlot?.startTime || !newSlot?.quantity) return;

    updateAppointmentSlot(
      {
        id: newSlot?.id as string,
        date: newSlot?.startTime as Date,
        quantity: newSlot?.quantity as number,
      },
      { onSuccess: close }
    );
  };

  useEffect(() => {
    if (slot) {
      setCurrentSlot(slot);
      setNewSlot(slot);
    }
  }, [slot]);

  if (!slot.startTime) return null;

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("edit-appointment-slot")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}
      styles={{ body: { minHeight: "auto" } }}>
      <Stack>
        <TextInput
          label="Date"
          withAsterisk
          disabled
          value={slot.startTime?.toLocaleDateString(i18n.language, {
            dateStyle: "full",
          })}
        />
        <Select
          label="Time"
          withAsterisk
          placeholder="Select a time slot"
          // data={
          //   [
          //   {
          //     label: i18n.language === "fr" ? "8h00" : "8:00 AM",
          //     value: "8",
          //     disabled: getIsTimeSlotDisabled(
          //       slots,
          //       currentSlot?.startTime as Date,
          //       8
          //     ),
          //   },
          //   {
          //     label: i18n.language === "fr" ? "10h00" : "10:00 AM",
          //     value: "10",
          //     disabled: getIsTimeSlotDisabled(
          //       slots,
          //       currentSlot?.startTime as Date,
          //       10
          //     ),
          //   },
          //   {
          //     label: i18n.language === "fr" ? "12h00" : "12:00 PM",
          //     value: "12",
          //     disabled: getIsTimeSlotDisabled(
          //       slots,
          //       currentSlot?.startTime as Date,
          //       12
          //     ),
          //   },
          //   {
          //     label: i18n.language === "fr" ? "14h00" : "2:00 PM",
          //     value: "14",
          //     disabled: getIsTimeSlotDisabled(
          //       slots,
          //       currentSlot?.startTime as Date,
          //       14
          //     ),
          //   },
          //   {
          //     label: i18n.language === "fr" ? "16h00" : "4:00 PM",
          //     value: "16",
          //     disabled: getIsTimeSlotDisabled(
          //       slots,
          //       currentSlot?.startTime as Date,
          //       16
          //     ),
          //   },
          //   {
          //     label: i18n.language === "fr" ? "18h00" : "6:00 PM",
          //     value: "18",
          //     disabled: getIsTimeSlotDisabled(
          //       slots,
          //       currentSlot?.startTime as Date,
          //       18
          //     ),
          //   },
          // ]}
          data={timeSlotData}
          allowDeselect={false}
          value={newSlot?.startTime?.getHours().toString()}
          onChange={val => {
            setNewSlot(v => ({
              ...v,
              startTime: new Date(
                (newSlot?.startTime as Date).setHours(parseInt(val as string))
              ),
            }));
          }}
        />

        <NumberInput
          min={1}
          label={t("quantity")}
          description={t("quantity-description")}
          withAsterisk
          value={newSlot?.quantity}
          onChange={val => {
            setNewSlot(v => ({ ...v, quantity: parseInt(val as string) }));
          }}
        />

        <Group mt="xl" justify="end">
          <PermissionGate permission="appointments.slots"><Button
            disabled={!newSlot?.startTime || !newSlot?.quantity}
            loading={isUpdateAppointmentSlotLoading}
            onClick={handleUpdateAppointmentSlot}>
            {t("update")}
          </Button></PermissionGate>
        </Group>
      </Stack>
    </Modal>
  );
};
